import { FC, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { H3, Small } from '@comeon/ui';
import { CarouselItem } from './sportsbook-promotion-carousel-item-base';
import { PromotionItemLink as PromotionLinkType } from './sportsbook-promotion-carousel-types';
import { useTypedTranslations } from '../../../../translation/use-translations';
import { Link } from '~/utils/navigation';
import PromotionTextKeys, { PromotionTranslations } from './sportsbook-promotion-carousel-text-keys';
import getSrcsetForResource from '../../utils/get-srcset-for-resource';

const PROMO_IMAGE_WIDTH = 640;

const textKeyHasValue = (textKey?: string) =>
	textKey !== undefined && textKey.trim().length > 0 && !textKey.startsWith('??') && !textKey.endsWith('??');

const hasResourceBeenUploaded = (input?: string) => {
	if (!input) {
		return false;
	}

	const keywords = ['default_dynamic_image_key', 'temp_game_icon_key'];
	const normalizedInput = input.trim().toLowerCase();

	return keywords.every((keyword: string) => !normalizedInput.includes(keyword));
};

const LinkExternal = styled(Link)`
	width: 100%;
	overflow: hidden;
	border-radius: var(--base-radius-large);
`;

type PromotionItemProps = {
	hasCasinoLinkLayout?: boolean;
};

const PromotionLinkItem = styled(CarouselItem)<PromotionItemProps>`
	justify-content: flex-end;
	padding: 0;
	background-color: var(--main-surface-1);

	${({ hasCasinoLinkLayout }) =>
		hasCasinoLinkLayout &&
		css`
			padding: 12px;
			justify-content: flex-start;
		`}
`;

const PromotionLinkDescription = styled(Small)`
	padding: 8px;
	border-bottom-left-radius: var(--base-radius-large);
	border-bottom-right-radius: var(--base-radius-large);
	background-color: var(--generic-overlay);
	backdrop-filter: blur(5px);
	color: var(--main-text-light);
`;

const PromotionLinkTitle = styled(H3)`
	z-index: 1;
	color: var(--main-text-light);
`;

const PromotionPicture = styled.picture`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
`;

const PromotionImg = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: top;
`;

type DynamicTranslations = {
	url: string;
	description: string;
	title: string;
};

/** Prevent the user from accidentally clicking a link in the carousel when we deem the intent was to scroll horizontally with a mouse */
function usePreventClickWhenScrolling() {
	const ref = useRef<HTMLAnchorElement>(null);
	const xTravelDistance = useRef(0);
	const [shouldPreventDefault, setShouldPreventDefault] = useState(false);

	const onMouseDown = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		if (!ref.current) {
			return;
		}

		xTravelDistance.current = event.pageX - ref.current.offsetLeft;
	};

	const onMouseUp = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		if (!ref.current) {
			return;
		}

		const startX = xTravelDistance.current;
		const endX = event.pageX - ref.current.offsetLeft;

		// 10 is just an arbitrary number where we decide that the gesture was intended as a scroll. Anything less than that is probably a click
		if (Math.abs(startX - endX) > 10) {
			xTravelDistance.current = endX;
			setShouldPreventDefault(true);
			return;
		}

		xTravelDistance.current = endX;
		setShouldPreventDefault(false);
	};

	const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		if (shouldPreventDefault) {
			event.preventDefault();
		}
	};

	return { onMouseDown, onMouseUp, onClick, ref };
}

type Props = {
	promotion: PromotionLinkType;
	staticTranslations: PromotionTranslations;
	resources: Record<string, string>;
	index: number;
	onItemClick?: (index: number) => void;
};

const PromotionItemLink: FC<Props> = ({ promotion, staticTranslations, resources, index, onItemClick }) => {
	const dynamicTranslations: DynamicTranslations = {
		title: promotion.titleTextKey,
		description: promotion.descriptionTextKey,
		url: promotion.linkTextKey
	};

	const typedTranslations = useTypedTranslations(dynamicTranslations);
	const shouldPreventClickProps = usePreventClickWhenScrolling();

	// Absolute Urls might cause a full page refresh if they dont include the language param.
	// This will convert the url into a possible relative url
	const normalizedUrl = useMemo(() => {
		if (typedTranslations.isLoadingTranslations) {
			return '';
		}

		const url = promotion.linkUrl ? promotion.linkUrl : typedTranslations.translations[dynamicTranslations.url];
		const { origin } = window.location;

		if (url.includes(origin)) {
			return url.substring(origin.length);
		}
		return url;
	}, [
		dynamicTranslations.url,
		promotion.linkUrl,
		typedTranslations.isLoadingTranslations,
		typedTranslations.translations
	]);

	if (typedTranslations.isLoadingTranslations) {
		return <PromotionLinkItem />;
	}

	const description = typedTranslations.translations[dynamicTranslations.description];
	const title = typedTranslations.translations[dynamicTranslations.title];
	/** Checking if the title, description and resource has any values added from BO. This way we can show/hide elements from BO if we want to */
	const hasTitle = textKeyHasValue(title);
	const hasDescription = textKeyHasValue(description);

	const shouldUseImageUrlOverImageResourceKey = !!promotion.backgroundImageUrl;
	const hasResource = hasResourceBeenUploaded(resources[promotion.backgroundImageResourceKey]);
	const backgroundImageSource = hasResource ? resources[promotion.backgroundImageResourceKey] : undefined;

	/** Display a "Custom card" which uses dynamic content and has slightly different layout than casino links */
	return (
		<LinkExternal href={normalizedUrl} rel="noreferrer" {...shouldPreventClickProps}>
			<PromotionLinkItem onClick={onItemClick?.bind(this, index)}>
				{shouldUseImageUrlOverImageResourceKey ? (
					<PromotionPicture>
						<PromotionImg
							src={`${promotion.backgroundImageUrl}?format=auto&width=${PROMO_IMAGE_WIDTH}`}
							alt={hasTitle ? title : `${staticTranslations[PromotionTextKeys.IMAGE_ALT]} ${index + 1}`}
						/>
					</PromotionPicture>
				) : (
					backgroundImageSource && (
						<PromotionPicture>
							<source
								srcSet={getSrcsetForResource(backgroundImageSource, [PROMO_IMAGE_WIDTH], 'webp')}
								type="image/webp"
							/>
							<source
								srcSet={getSrcsetForResource(backgroundImageSource, [PROMO_IMAGE_WIDTH], 'jpeg')}
								type="image/jpeg"
							/>
							<source
								srcSet={getSrcsetForResource(backgroundImageSource, [PROMO_IMAGE_WIDTH], 'png')}
								type="image/png"
							/>
							<PromotionImg
								src={backgroundImageSource}
								alt={
									hasTitle ? title : `${staticTranslations[PromotionTextKeys.IMAGE_ALT]} ${index + 1}`
								}
							/>
						</PromotionPicture>
					)
				)}
				{hasTitle && <PromotionLinkTitle>{title}</PromotionLinkTitle>}
				{hasDescription && <PromotionLinkDescription>{description}</PromotionLinkDescription>}
			</PromotionLinkItem>
		</LinkExternal>
	);
};

export default PromotionItemLink;
